import { ClaimParentCard } from "@/components/parent/ClaimParentCard";
import { MyRentalsPanel } from "@/components/parent/MyRentalsPanel";
import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, ChevronLeft, ChevronRight, Clock, MapPin, Loader2, CalendarDays, Copy, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { listMyStudentTCs, getMyParentInfo } from "@/lib/api/parent-invites.functions";
import {
  getPublicRentalSettings,
  listPublicRentalAvailability,
  createPublicRentalReservation,
  getMyRentalPaymentLink,
} from "@/lib/api/rentals.functions";

import { paymentMethodLabel, paymentMethodChannel } from "@/lib/payment-methods";
import { brIsoDate, brMinutes, brDow, brTime, brDateLabel, brIsoFrom, brToday, brStartOfWeek, addAnchorDays, anchorIso, brDayAnchor } from "@/lib/tz";

export const Route = createFileRoute("/_authenticated/responsavel/reserva")({
  component: ParentReservationPage,
});

const ACTIVE_KEY = "futbux:activeParentTc";
const DAY_LABELS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function formatTime(d: Date | string) {
  return brTime(d);
}

function parseTime(t: string) {
  const [h, m] = t.split(":").map(Number);
  return { h, m };
}

function toMin(t: string) {
  const [h, m] = String(t).slice(0, 5).split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

function minutesToHHMM(min: number) {
  return `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
}

function ParentReservationPage() {
  const navigate = useNavigate();
  const listTcs = useServerFn(listMyStudentTCs);
  const getSettings = useServerFn(getPublicRentalSettings);
  const getAvailability = useServerFn(listPublicRentalAvailability);
  const createReservation = useServerFn(createPublicRentalReservation);
  const payLinkFn = useServerFn(getMyRentalPaymentLink);

  // Gera (e reaproveita) o link de pagamento de uma reserva.
  const ensurePayLink = async (rentalId: string) => {
    if (payLinks[rentalId]) return payLinks[rentalId];
    setPayBusyId(rentalId);
    try {
      const res: any = await payLinkFn({ data: { rentalId } });
      setPayLinks((prev) => ({ ...prev, [rentalId]: res.link }));
      return res.link as string;
    } finally {
      setPayBusyId(null);
    }
  };

  const copyPayLink = async (rentalId: string) => {
    try {
      const link = await ensurePayLink(rentalId);
      await navigator.clipboard.writeText(link);
      toast.success("Link de pagamento copiado!");
    } catch (e: any) {
      toast.error(e?.message ?? "Não foi possível gerar o link");
    }
  };

  const sendPayLinkWhatsapp = async (rentalId: string) => {
    try {
      const link = await ensurePayLink(rentalId);
      const text = encodeURIComponent(`Olá! Segue o link de pagamento da minha reserva: ${link}`);
      window.open(`https://wa.me/?text=${text}`, "_blank");
    } catch (e: any) {
      toast.error(e?.message ?? "Não foi possível gerar o link");
    }
  };


  const [tcId, setTcId] = useState<string | null>(null);
  const [tcName, setTcName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState<Date>(() => brStartOfWeek(brToday()));
  const [settings, setSettings] = useState<any>(null);
  const [modalities, setModalities] = useState<any[]>([]);
  const [prices, setPrices] = useState<any[]>([]);
  const [busy, setBusy] = useState<any>({});
  const [myRentals, setMyRentals] = useState<any[]>([]);
  const [tab, setTab] = useState<"reservar" | "minhas">("reservar");
  const [selectedModality, setSelectedModality] = useState<string>("all");

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const [rentalType, setRentalType] = useState<"avulsa" | "mensal" | "anual">("avulsa");
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientCpf, setClientCpf] = useState("");
  const [notes, setNotes] = useState("");
  const [parentId, setParentId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [createdRental, setCreatedRental] = useState<any>(null);
  const [sameDayOpen, setSameDayOpen] = useState(false);
  const [sameDayAck, setSameDayAck] = useState(false);
  const [payLinks, setPayLinks] = useState<Record<string, string>>({});
  const [payBusyId, setPayBusyId] = useState<string | null>(null);

  const getParent = useServerFn(getMyParentInfo);

  useEffect(() => {
    (async () => {
      try {
        const stored = (() => { try { return localStorage.getItem(ACTIVE_KEY); } catch { return null; } })();
        const tcsRes = await listTcs();
        const tcs = tcsRes.tcs ?? [];
        if (tcs.length === 0) {
          setLoading(false);
          return;
        }
        let active = tcs.find((t: any) => t.training_center_id === stored) ?? tcs[0];
        if (tcs.length > 1 && !stored) {
          navigate({ to: "/selecionar-ct" });
          return;
        }
        setTcId(active.training_center_id);
        setTcName(active.tc_name);
        try { localStorage.setItem(ACTIVE_KEY, active.training_center_id); } catch {}

        const parentRes = await getParent({ data: { tcId: active.training_center_id } });
        if (parentRes.parent) {
          setParentId(parentRes.parent.id);
          setClientName(parentRes.parent.name || "");
          setClientPhone(parentRes.parent.phone || "");
          setClientCpf((parentRes.parent as any).cpf || "");
        }
      } catch (e: any) {
        toast.error(e?.message || "Erro ao carregar centros");
      }
    })();
  }, []);

  useEffect(() => {
    if (!tcId) return;
    (async () => {
      setLoading(true);
      try {
        const settingsRes = await getSettings({ data: { training_center_id: tcId } });
        setSettings(settingsRes.settings);
        if (!settingsRes.settings) {
          setModalities([]); setPrices([]); setBusy({});
          setLoading(false);
          return;
        }
        const weekEnd = addAnchorDays(weekStart, 7);
        const availRes = await getAvailability({
          data: {
            training_center_id: tcId,
            weekStart: brIsoFrom(anchorIso(weekStart), 0),
            weekEnd: brIsoFrom(anchorIso(weekEnd), 0),
          },
        });
        setModalities(availRes.modalities ?? []);
        setSelectedModality((cur) => {
          const list = availRes.modalities ?? [];
          if (cur !== "all" && list.some((m: any) => m.id === cur)) return cur;
          return list[0]?.id ?? "all";
        });
        setPrices(availRes.prices ?? []);
        setBusy(availRes.busy ?? {});
        setMyRentals((availRes as any).myRentals ?? []);
      } catch (e: any) {
        toast.error(e?.message || "Erro ao carregar reservas");
      } finally {
        setLoading(false);
      }
    })();
  }, [tcId, weekStart]);

  const filteredModalities = useMemo(() => {
    const found = modalities.filter((m) => m.id === selectedModality);
    return found.length > 0 ? found : modalities.slice(0, 1);
  }, [modalities, selectedModality]);

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }).map((_, i) => addAnchorDays(weekStart, i));
  }, [weekStart]);

  const slotMins = useMemo(() => {
    if (!settings) return [] as number[];
    const slotMin = settings.slot_duration_min || 60;
    const step = settings.allow_half_hour_slots === false ? slotMin : Math.min(30, slotMin);
    const { h: startH, m: startM } = parseTime(settings.start_time || "06:00");
    const { h: endH, m: endM } = parseTime(settings.end_time || "23:00");
    const s = startH * 60 + startM;
    const e = endH * 60 + endM;
    const out: number[] = [];
    for (let m = s; m + slotMin <= e; m += step) out.push(m);
    return out;
  }, [settings]);

  const calCols = useMemo(
    () => (filteredModalities.length > 0 ? filteredModalities : [null]),
    [filteredModalities],
  );

  // Ocupações por dia (anônimas, exceto as do próprio usuário)
  const dayBusy = useMemo(() => {
    const rentals = busy.rentals ?? [];
    const trainings = busy.trainings ?? [];
    const events = busy.events ?? [];
    const blackouts = busy.blackouts ?? [];
    const mineById = new Map<string, any>((myRentals ?? []).map((r: any) => [r.id, r]));

    return weekDays.map((day, dow) => {
      const iso = anchorIso(day);
      const items: any[] = [];

      for (const r of rentals) {
        if (brIsoDate(r.start_at) !== iso) continue;
        const mine = !!r.mine && !!r.rental_id ? mineById.get(r.rental_id) ?? null : null;
        items.push({
          kind: mine ? "mine" : "busy",
          modality_id: r.modality_id ?? null,
          startMin: brMinutes(r.start_at),
          endMin: brMinutes(r.end_at),
          myRental: mine,
        });
      }
      for (const tr of trainings) {
        if (tr.day_of_week !== dow) continue;
        items.push({
          kind: "busy", modality_id: tr.modality_id ?? null,
          startMin: toMin(tr.start_time), endMin: toMin(tr.end_time),
        });
      }
      for (const ev of events) {
        if (!ev.match_date || String(ev.match_date).slice(0, 10) !== iso) continue;
        if (!ev.start_time || !ev.end_time) continue;
        items.push({
          kind: "busy", modality_id: ev.modality_id ?? null,
          startMin: toMin(ev.start_time), endMin: toMin(ev.end_time),
        });
      }
      for (const bl of blackouts) {
        const match = bl.kind === "one_off" ? bl.block_date === iso : bl.day_of_week === dow;
        if (!match) continue;
        items.push({
          kind: "busy", modality_id: bl.modality_id ?? null,
          startMin: toMin(bl.start_time), endMin: toMin(bl.end_time),
        });
      }
      return items;
    });
  }, [busy, weekDays, myRentals]);

  const busyIn = (dayIdx: number, modId: string | null, slotStart: number, slotEnd: number) =>
    (dayBusy[dayIdx] ?? [])
      .filter((b) => modId == null || b.modality_id == null || b.modality_id === modId)
      .filter((b) => b.startMin < slotEnd && b.endMin > slotStart)
      .sort((a, b) => a.startMin - b.startMin);

  const buildSlot = (day: Date, modality: any, slotStart: number, slotEnd: number) => {
    const iso = anchorIso(day);
    const start_at = brIsoFrom(iso, slotStart);
    const end_at = brIsoFrom(iso, slotEnd);
    return {
      day, dow: brDow(day), iso,
      start: new Date(start_at), end: new Date(end_at),
      start_at, end_at,
      modality: modality ?? { id: null, name: "Espaço" },
      durationMin: slotEnd - slotStart,
    };
  };

  function findPrice(prices: any[], modalityId: string | null, type: string, dow: number, durationMin: number) {
    const candidates = prices.filter((p) => {
      if (p.modality_id && p.modality_id !== modalityId) return false;
      if (p.rental_type !== type) return false;
      if (p.duration_min && p.duration_min !== durationMin) return false;
      if (p.day_of_week !== null && p.day_of_week !== dow) return false;
      return true;
    });
    return candidates[0] ?? null;
  }

  const openReserve = (slot: any) => {
    setSelectedSlot(slot);
    setRentalType("avulsa");
    setPaymentMethod(settings?.public_payment_methods?.[0] ?? "");
    setNotes("");
    setCreatedRental(null);
    setSameDayAck(false);
    setSameDayOpen(false);

    setOpenDialog(true);
  };

  // Abre o diálogo já no primeiro horário livre (opcionalmente de uma quadra).
  const openFirstFree = (modalityId?: string | null) => {
    const cols = modalityId ? calCols.filter((m: any) => m?.id === modalityId) : calCols;
    const dur = settings?.slot_duration_min || 60;
    for (let dayIdx = 0; dayIdx < weekDays.length; dayIdx++) {
      for (const slotStart of slotMins) {
        for (const mod of (cols.length ? cols : calCols)) {
          const slotEnd = slotStart + dur;
          const items = busyIn(dayIdx, mod?.id ?? null, slotStart, slotEnd);
          const fullyBusy = items.some((b) => b.startMin <= slotStart && b.endMin >= slotEnd);
          if (!fullyBusy) {
            openReserve(buildSlot(weekDays[dayIdx], mod, slotStart, slotEnd));
            return;
          }
        }
      }
    }
    toast.info("Não há horários livres nesta semana. Avance para a próxima semana.");
  };


  // Ajustes de data/hora dentro do diálogo de reserva
  const changeSlotDate = (iso: string) => {
    if (!selectedSlot || !iso) return;
    const startMin = brMinutes(selectedSlot.start_at);
    const dur = selectedSlot.durationMin;
    setSelectedSlot({
      ...selectedSlot,
      day: brDayAnchor(iso),
      iso,
      dow: brDow(brDayAnchor(iso)),
      start_at: brIsoFrom(iso, startMin),
      end_at: brIsoFrom(iso, startMin + dur),
      start: new Date(brIsoFrom(iso, startMin)),
      end: new Date(brIsoFrom(iso, startMin + dur)),
    });
  };

  const changeSlotStart = (min: number) => {
    if (!selectedSlot) return;
    const iso = selectedSlot.iso;
    const dur = selectedSlot.durationMin;
    setSelectedSlot({
      ...selectedSlot,
      start_at: brIsoFrom(iso, min),
      end_at: brIsoFrom(iso, min + dur),
      start: new Date(brIsoFrom(iso, min)),
      end: new Date(brIsoFrom(iso, min + dur)),
    });
  };

  const changeSlotEnd = (min: number) => {
    if (!selectedSlot) return;
    const iso = selectedSlot.iso;
    const startMin = brMinutes(selectedSlot.start_at);
    if (min <= startMin) return;
    setSelectedSlot({
      ...selectedSlot,
      end_at: brIsoFrom(iso, min),
      end: new Date(brIsoFrom(iso, min)),
      durationMin: min - startMin,
    });
  };

  const minutesUntilStart = () =>
    selectedSlot ? Math.round((new Date(selectedSlot.start_at).getTime() - Date.now()) / 60000) : 0;

  const onReserve = async () => {
    const cpfDigits = clientCpf.replace(/\D/g, "");
    const phoneDigits = clientPhone.replace(/\D/g, "");
    const faltando: string[] = [];
    if (clientName.trim().split(/\s+/).length < 2) faltando.push("nome completo");
    if (phoneDigits.length < 10) faltando.push("telefone com DDD");
    if (cpfDigits.length !== 11) faltando.push("CPF");
    if (!tcId || !selectedSlot) return;
    if (faltando.length > 0) {
      toast.error(`Só é possível reservar com os dados completos. Faltando: ${faltando.join(", ")}.`);
      return;
    }
    if (!paymentMethod) {
      toast.error("Selecione uma forma de pagamento.");
      return;
    }
    // Não permitir reservas em data/horário já passados
    if (minutesUntilStart() <= 0) {
      toast.error("Escolha uma data e um horário futuros — não é possível reservar horário que já passou.");
      return;
    }
    // Reserva para hoje: confirmar com o usuário
    if (selectedSlot.iso === brToday() && !sameDayAck) {
      setSameDayOpen(true);
      return;
    }
    await doReserve();
  };

  const doReserve = async () => {
    const cpfDigits = clientCpf.replace(/\D/g, "");
    const phoneDigits = clientPhone.replace(/\D/g, "");
    if (!tcId || !selectedSlot) return;

    setSaving(true);
    try {
      const price = computePrice(selectedSlot, rentalType, prices);
      const deposit = settings?.require_deposit ? Math.round(price * (settings.deposit_percent / 100) * 100) / 100 : 0;
      const balance = Math.max(0, price - deposit);

      const res = await createReservation({
        data: {
          training_center_id: tcId,
          modality_id: selectedSlot.modality?.id ?? null,
          rental_type: rentalType,
          start_at: selectedSlot.start_at,
          end_at: selectedSlot.end_at,
          payment_method: paymentMethod,
          client_name: clientName.trim(),
          client_phone: phoneDigits,
          client_cpf: cpfDigits,
          notes: notes.trim() || null,
          price,
          deposit_amount: deposit,
          balance_amount: balance,
          parent_id: parentId ?? undefined,
        },
      });

      setCreatedRental(res);
      toast.success(
        `Reserva solicitada! Total: R$ ${price.toFixed(2)}${deposit > 0 ? ` · Sinal: R$ ${deposit.toFixed(2)}` : ""}`,
        { duration: 7000 },
      );
      if (settings?.auto_confirm_after_payment && deposit === 0) {
        toast.success("Reserva confirmada.");
      }
    } catch (e: any) {
      toast.error(e?.message || "Erro ao criar reserva");
    } finally {
      setSaving(false);
    }
  };

  function computePrice(slot: any, type: string, priceList: any[]) {
    const base = findPrice(priceList, slot.modality?.id ?? null, type, slot.dow, slot.durationMin);
    if (!base) return 0;
    return Math.max(0, Number(base.price ?? 0) - Number(base.discount ?? 0));
  }

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Carregando…</div>;
  }

  if (!tcId) {
    return <ClaimParentCard />;
  }

  const readOnly = !settings?.allow_public_booking || (settings?.public_payment_methods ?? []).length === 0;

  if (!settings) {
    return (
      <div className="p-6 max-w-md mx-auto">
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            O centro <strong>{tcName}</strong> ainda não configurou a locação de espaços.
          </CardContent>
        </Card>
      </div>
    );
  }


  return (
    <div className="p-4 space-y-4 max-w-5xl mx-auto">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Calendar className="w-6 h-6 text-primary" /> Reserva de Espaço</h1>
          <p className="text-sm text-muted-foreground">{tcName}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setWeekStart((w) => addAnchorDays(w, -7))}><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-medium">
            {brDateLabel(weekDays[0])} – {brDateLabel(weekDays[6])}
          </span>
          <Button variant="outline" size="sm" onClick={() => setWeekStart((w) => addAnchorDays(w, 7))}><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      {readOnly && (
        <div className="text-sm rounded-md border border-amber-500 bg-amber-50 text-amber-800 px-3 py-2">
          Reservas pelo app estão <b>indisponíveis</b> no momento. Você pode consultar os valores e os horários livres/ocupados abaixo — para reservar, fale com o centro <b>{tcName}</b>.
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          variant={tab === "reservar" ? "default" : "outline"}
          size="sm"
          onClick={() => { setTab("reservar"); if (tab === "reservar" && !readOnly) openFirstFree(selectedModality || null); }}
        >
          Reservar
        </Button>
        <Button variant={tab === "minhas" ? "default" : "outline"} size="sm" onClick={() => setTab("minhas")}>
          Minhas reservas
        </Button>
      </div>


      {tab === "minhas" && <MyRentalsPanel />}

      {tab === "reservar" && (
      <div className="space-y-4">
      <div className="flex items-end gap-3 flex-wrap">

        <div>
          <Label className="text-sm whitespace-nowrap">Quadra / Espaço</Label>
          <Select value={selectedModality} onValueChange={setSelectedModality}>
            <SelectTrigger className="w-60">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {modalities.map((m) => (
                <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm">Ir para a data</Label>
          <Input
            type="date"
            className="w-44"
            value={anchorIso(weekStart)}
            onChange={(e) => {
              if (!e.target.value) return;
              setWeekStart(brStartOfWeek(e.target.value));
            }}
          />
        </div>
        <Button variant="outline" size="sm" onClick={() => setWeekStart(brStartOfWeek(brToday()))}>
          Semana atual
        </Button>
      </div>

      {/* Valores de locação cadastrados pelo centro (consulta) */}
      {prices.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Valores de locação</CardTitle>
            <CardDescription>Tabela informada pelo centro de treinamento. Escolha um horário livre no calendário para reservar.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-2 sm:grid-cols-2">
            {prices.map((p: any, i: number) => {
              const mod = modalities.find((m: any) => m.id === p.modality_id);
              return (
                <div key={i} className="border rounded-md p-3 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{mod?.name ?? "Espaço"}</span>
                    <Badge variant="outline" className="capitalize">{p.rental_type}</Badge>
                  </div>
                  <div className="text-lg font-bold text-primary">
                    R$ {Number(p.price || 0).toFixed(2)}
                    {p.rental_type === "mensal" && <span className="text-sm font-medium text-amber-700"> / por locação</span>}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Duração: {p.duration_min ?? 60} min
                    {p.day_of_week !== null && p.day_of_week !== undefined ? ` · ${DAY_LABELS[p.day_of_week]}` : ""}
                  </div>
                  {p.rental_type === "mensal" && (
                    <div className="text-xs rounded-md border border-amber-500 bg-amber-50 text-amber-800 px-2 py-1">
                      Periodicidade mensal: valor cobrado <b>para cada locação</b> do mês.
                    </div>
                  )}
                  {!readOnly && (
                    <Button
                      size="sm"
                      className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                      onClick={() => {
                        if (p.modality_id) setSelectedModality(p.modality_id);
                        openFirstFree(p.modality_id ?? null);
                      }}

                    >
                      Reservar horário
                    </Button>
                  )}


                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {modalities.length === 0 && (
        <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">Nenhum espaço disponível para reserva.</CardContent></Card>
      )}

      <div id="calendario-reserva" className="overflow-auto">
        <table className="w-full border-collapse text-xs table-fixed">
          <thead>
            <tr>
              <th className="border p-1 bg-muted/50 w-14" rowSpan={calCols.length > 1 ? 2 : 1}>Hora</th>
              {weekDays.map((d, i) => {
                const today = anchorIso(d) === brToday();
                return (
                  <th key={i} colSpan={calCols.length} className={`border p-1 ${today ? "bg-primary/10" : "bg-muted/50"}`}>
                    {DAY_LABELS[i]}<br />
                    <span className="font-normal">{brDateLabel(d).slice(0, 5)}</span>
                  </th>
                );
              })}
            </tr>
            {calCols.length > 1 && (
              <tr>
                {weekDays.flatMap((_, i) =>
                  calCols.map((m: any) => (
                    <th key={`${i}-${m?.id ?? "x"}`} className="border p-0.5 bg-muted/30 font-normal text-[10px] truncate">
                      {m?.name ?? "Espaço"}
                    </th>
                  )),
                )}
              </tr>
            )}
          </thead>
          <tbody>
            {slotMins.map((slotStart) => {
              const dur = settings?.slot_duration_min || 60;
              const slotEnd = slotStart + dur;
              return (
                <tr key={slotStart}>
                  <td className="border p-1 text-center font-mono bg-muted/30">{minutesToHHMM(slotStart)}</td>
                  {weekDays.flatMap((day, dayIdx) =>
                    calCols.map((mod: any) => {
                      const items = busyIn(dayIdx, mod?.id ?? null, slotStart, slotEnd);
                      const fullyBusy = items.some((b) => b.startMin <= slotStart && b.endMin >= slotEnd);
                      return (
                        <td key={`${dayIdx}-${mod?.id ?? "x"}`} className="border p-0 align-top">
                          <div className="relative h-12">
                            {!fullyBusy && (
                              <button
                                disabled={readOnly}
                                onClick={() => { if (!readOnly) openReserve(buildSlot(day, mod, slotStart, slotEnd)); }}
                                className="absolute inset-0 bg-green-50 hover:bg-green-100 text-green-700 text-left p-1 disabled:cursor-default disabled:hover:bg-green-50"
                              >
                                {readOnly ? "Livre" : "+ Reservar"}
                              </button>
                            )}
                            {items.map((b, bi) => {
                              const top = ((Math.max(b.startMin, slotStart) - slotStart) / dur) * 100;
                              const height = ((Math.min(b.endMin, slotEnd) - Math.max(b.startMin, slotStart)) / dur) * 100;
                              const mine = b.kind === "mine";
                              return (
                                <div
                                  key={bi}
                                  className={`absolute left-0 right-0 overflow-hidden border px-1 leading-tight ${mine ? "bg-emerald-100 border-emerald-400 text-emerald-800" : "bg-zinc-200 border-zinc-300 text-muted-foreground"}`}
                                  style={{ top: `${top}%`, height: `${height}%` }}
                                  title={`${minutesToHHMM(b.startMin)}–${minutesToHHMM(b.endMin)}`}
                                >
                                  <div className="font-medium truncate">{mine ? "Sua reserva" : "Ocupado"}</div>
                                  {mine && (
                                    <div className="text-[10px] truncate">
                                      R$ {Number(b.myRental?.price_paid ?? 0).toFixed(2)} · {b.myRental?.paid ? "Pago" : "Pendente"}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      );
                    }),
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Minhas reservas nesta semana */}
      {myRentals.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><CalendarDays className="w-4 h-4" /> Minhas reservas na semana</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {myRentals.map((r: any) => {
              const mod = modalities.find((m: any) => m.id === r.modality_id);
              return (
                <div key={r.id} className="border rounded-md p-3 text-sm space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-medium">{mod?.name ?? "Espaço"}</div>
                      <div className="text-xs text-muted-foreground">
                        {brDateLabel(r.start_at)} · {brTime(r.start_at)} – {brTime(r.end_at)} · {paymentMethodLabel(r.payment_method || "")}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">R$ {Number(r.price_paid ?? 0).toFixed(2)}</div>
                      <Badge className={r.paid ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"}>
                        {r.paid ? "Pago" : "Pendente"}
                      </Badge>
                    </div>
                  </div>
                  {!r.paid && (
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" disabled={payBusyId === r.id} onClick={() => copyPayLink(r.id)}>
                        {payBusyId === r.id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Copy className="h-3 w-3 mr-1" />}
                        Copiar link de pagamento
                      </Button>
                      <Button size="sm" variant="outline" disabled={payBusyId === r.id} onClick={() => sendPayLinkWhatsapp(r.id)}>
                        <MessageCircle className="h-3 w-3 mr-1" /> Solicitar link por WhatsApp
                      </Button>
                    </div>
                  )}
                  {payLinks[r.id] && (
                    <div className="text-xs font-mono bg-muted p-2 rounded-md break-all">{payLinks[r.id]}</div>
                  )}
                </div>

              );
            })}
          </CardContent>
        </Card>
      )}
      </div>
      )}



      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar Reserva</DialogTitle>
          </DialogHeader>
          {createdRental ? (
            <div className="space-y-4">
              <div className="p-3 rounded-md bg-emerald-50 text-emerald-800 text-sm space-y-1">
                <p>Reserva #{createdRental.rental.id.slice(0, 8)} solicitada com sucesso.</p>
                <p><strong>Valor total:</strong> R$ {Number(createdRental.rental.price_paid ?? 0).toFixed(2)}</p>
                {createdRental.deposit > 0 && (
                  <p><strong>Sinal a pagar:</strong> R$ {Number(createdRental.deposit).toFixed(2)}</p>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                A cobrança foi gerada e enviada por WhatsApp. A reserva só é confirmada após o pagamento
                {paymentMethod ? ` (${paymentMethodLabel(paymentMethod)})` : ""}
                {paymentMethod && paymentMethodChannel(paymentMethod) === "comprovante"
                  ? " — pague com a chave/Pix copia-e-cola enviada e devolva o comprovante pelo WhatsApp."
                  : paymentMethod && paymentMethodChannel(paymentMethod) === "mercadopago"
                  ? " — use o link de pagamento enviado por WhatsApp."
                  : "."}
              </p>
              <p className="text-sm text-muted-foreground">
                Acompanhe o status na aba <strong>Minhas reservas</strong>.
              </p>
              <DialogFooter>
                <Button onClick={() => setOpenDialog(false)}>Fechar</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              {selectedSlot && (
                <div className="text-sm bg-muted p-3 rounded-md space-y-1">
                  <p><strong>Reserva feita por:</strong> {clientName || "—"}</p>
                  <p><strong>Espaço:</strong> {selectedSlot.modality.name}</p>
                  <p><strong>Data:</strong> {brDateLabel(selectedSlot.day)}</p>
                  <p><strong>Horário:</strong> {brTime(selectedSlot.start)} – {brTime(selectedSlot.end)}</p>
                  <p><strong>Periodicidade:</strong> {rentalType === "avulsa" ? "Avulsa" : rentalType === "mensal" ? "Mensal" : "Anual"}</p>
                  <p><strong>Valor total:</strong> R$ {computePrice(selectedSlot, rentalType, prices).toFixed(2)}</p>
                  {paymentMethod && (
                    <p><strong>Forma de pagamento:</strong> {paymentMethodLabel(paymentMethod)}</p>
                  )}
                </div>
              )}

              {selectedSlot && (
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label>Data</Label>
                    <Input
                      type="date"
                      min={brToday()}
                      value={selectedSlot.iso}
                      onChange={(e) => changeSlotDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Início</Label>
                    <Input
                      type="time"
                      value={brTime(selectedSlot.start_at)}
                      onChange={(e) => changeSlotStart(toMin(e.target.value))}
                    />
                  </div>
                  <div>
                    <Label>Fim</Label>
                    <Input
                      type="time"
                      value={brTime(selectedSlot.end_at)}
                      onChange={(e) => changeSlotEnd(toMin(e.target.value))}
                    />
                  </div>
                </div>
              )}


              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tipo de locação</Label>
                  <Select value={rentalType} onValueChange={(v) => setRentalType(v as any)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="avulsa">Avulsa</SelectItem>
                      <SelectItem value="mensal">Mensal</SelectItem>
                      <SelectItem value="anual">Anual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Forma de pagamento</Label>
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(settings?.public_payment_methods || []).map((m: string) => (
                        <SelectItem key={m} value={m}>{paymentMethodLabel(m)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {selectedSlot && (
                <div className="text-sm p-3 border rounded-md">
                  <div className="flex justify-between"><span>Valor total:</span><strong>R$ {computePrice(selectedSlot, rentalType, prices).toFixed(2)}</strong></div>
                  {settings?.require_deposit && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>Sinal ({settings.deposit_percent}%):</span>
                      <span>R$ {Math.round(computePrice(selectedSlot, rentalType, prices) * (settings.deposit_percent / 100) * 100 / 100).toFixed(2)}</span>
                    </div>
                  )}
                </div>
              )}

              <div>
                <Label>Nome completo *</Label>
                <Input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Seu nome completo" />
              </div>
              <div>
                <Label>Telefone (WhatsApp) *</Label>
                <Input value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} placeholder="(00) 00000-0000" />
              </div>
              <div>
                <Label>CPF *</Label>
                <Input
                  value={clientCpf}
                  onChange={(e) => setClientCpf(e.target.value)}
                  placeholder="000.000.000-00"
                  inputMode="numeric"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  A reserva só é confirmada com nome completo, telefone e CPF — os mesmos dados exigidos no cadastro de clientes do centro.
                </p>
              </div>

              <div>
                <Label>Observação (opcional)</Label>
                <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpenDialog(false)}>Cancelar</Button>
                <Button onClick={onReserve} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CalendarDays className="h-4 w-4 mr-2" />}
                  Solicitar Reserva
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmação de reserva no mesmo dia */}
      <Dialog open={sameDayOpen} onOpenChange={setSameDayOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reserva para hoje</DialogTitle>
          </DialogHeader>
          {selectedSlot && (
            <div className="text-sm space-y-2">
              <p>
                Esta reserva é para <strong>hoje</strong>, {brDateLabel(selectedSlot.day)} às{" "}
                <strong>{brTime(selectedSlot.start_at)}</strong>.
              </p>
              <p className="text-muted-foreground">
                Falta{" "}
                <strong>
                  {Math.floor(minutesUntilStart() / 60) > 0 ? `${Math.floor(minutesUntilStart() / 60)}h ` : ""}
                  {minutesUntilStart() % 60}min
                </strong>{" "}
                para o horário começar.
              </p>
              <p>Confirma que a reserva é para o mesmo dia?</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSameDayOpen(false)}>Cancelar</Button>
            <Button
              onClick={async () => {
                setSameDayAck(true);
                setSameDayOpen(false);
                await doReserve();
              }}
            >
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>

  );
}
